import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE, EXEC_APPENDER } from "../utils/constants";
import { fetchTextWithDiagnostics, safeArr } from "../utils/helpers";
import { Chip, SecondaryButton, Skeleton } from "../components/UI";

function normalizeChildList(attempt) {
  return (
    attempt?.lstChilds ||
    attempt?.lstChild ||
    attempt?.children ||
    attempt?.childs ||
    []
  );
}

function pickLatestAttempt(attempts) {
  const arr = safeArr(attempts);
  if (arr.length === 0) return 0;
  let bestIdx = 0;
  let bestExecId = Number(arr[0]?.exec_id ?? arr[0]?.execId ?? -1);
  for (let i = 1; i < arr.length; i++) {
    const execId = Number(arr[i]?.exec_id ?? arr[i]?.execId ?? -1);
    if (execId > bestExecId) {
      bestExecId = execId;
      bestIdx = i;
    }
  }
  return bestIdx;
}

function getSelectedChoice(child) {
  const keys = [1, 2, 3, 4];
  for (const idx of keys) {
    const chosen = child?.[`answer_choosen${idx}`] ?? child?.[`answerChoosen${idx}`];
    if (chosen === "Y" || chosen === true || chosen === 1 || chosen === "1") {
      return ["A", "B", "C", "D"][idx - 1];
    }
  }
  return "—";
}

function computeStats(childs) {
  let right = 0;
  let wrong = 0;
  let un = 0;

  for (const c of safeArr(childs)) {
    const rw = String(c?.right_or_wrong ?? "").toLowerCase().trim();
    if (rw === "right" || rw === "1" || rw === "yes") right++;
    else if (rw === "wrong" || rw === "0" || rw === "no") wrong++;
    else un++;
  }

  const total = right + wrong + un;
  const accuracy = total ? Math.round((right / total) * 100) : 0;
  return { total, right, wrong, un, accuracy };
}

export function ResultsViewer({
  token,
  appName,
  section,
  category,
  onBack,
  onUnauthorized,
  isDarkMode,
}) {
  const [attempts, setAttempts] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchResults = useCallback(async () => {
    if (!token || !appName || !section || !category) return;
    setLoading(true);
    setError("");

    try {
      const base = API_BASE.replace(/\/$/, "");
      const basePath =
        `${base}/exams${EXEC_APPENDER}/${encodeURIComponent(appName)}` +
        `/users/search_exam_exec${EXEC_APPENDER}`;

      const queryCore =
        `category_name=${encodeURIComponent(category)}` +
        `&section_name=${encodeURIComponent(section)}`;

      const urls = [
        `${basePath}?${queryCore}&is_completed=N`,
        `${basePath}?${queryCore}`,
      ];

      let found = [];
      let lastStatus = 200;

      for (const url of urls) {
        const res = await fetchTextWithDiagnostics(url, {
          headers: { Authorization: token },
        });

        if (res.status === 401) {
          onUnauthorized?.();
          return;
        }

        lastStatus = res.status;
        if (!res.ok) continue;

        try {
          const arr = JSON.parse(res.text);
          const list = Array.isArray(arr) ? arr : [];
          if (list.length > 0) {
            found = list;
            break;
          }
        } catch {
          continue;
        }
      }

      if (found.length === 0) {
        setAttempts([]);
        if (lastStatus !== 200) {
          setError(`Failed to load results (status ${lastStatus})`);
        }
        return;
      }

      setAttempts(found);
      setSelectedIdx(pickLatestAttempt(found));
    } catch (e) {
      setError(String(e?.message || "Failed to load results"));
      setAttempts([]);
    } finally {
      setLoading(false);
    }
  }, [token, appName, section, category, onUnauthorized]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const currentAttempt = attempts[selectedIdx] || null;
  const childs = useMemo(() => safeArr(normalizeChildList(currentAttempt)), [currentAttempt]);
  const stats = useMemo(() => computeStats(childs), [childs]);

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className={`text-sm font-medium hover:underline ${isDarkMode ? "text-indigo-300" : "text-indigo-700"}`}
          title="Return to category selection"
        >
          ← Back to categories
        </button>
        <Chip>
          {section} › {category}
        </Chip>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xl font-semibold text-slate-900">Results</div>
              <div className="text-sm text-slate-500 mt-1">Results for {category} in {section}</div>
            </div>

            <div className="flex items-center gap-2">
              <SecondaryButton onClick={fetchResults} disabled={loading} title="Reload your latest attempt results">
                {loading ? "Refreshing…" : "Refresh"}
              </SecondaryButton>

              <select
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm min-w-[220px]"
                value={selectedIdx}
                onChange={(e) => setSelectedIdx(Number(e.target.value))}
                disabled={loading || attempts.length === 0}
                title="Select an attempt to view its results"
              >
                {attempts.map((a, idx) => {
                  const execId = a?.exec_id ?? a?.execId ?? a?.id ?? idx;
                  return (
                    <option key={String(execId)} value={idx}>
                      Attempt {idx + 1} • exec {execId}
                    </option>
                  );
                })}
                {attempts.length === 0 ? <option value={0}>No attempts</option> : null}
              </select>
            </div>
          </div>

          {error ? (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
          ) : null}
        </div>

        <div className="px-6 py-6">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-1/3" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : attempts.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-700">
              No results found for this category.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-tour="results-summary">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs text-slate-500">Total</div>
                  <div className="text-xl font-semibold text-slate-900">{stats.total}</div>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-xs text-emerald-700">Right</div>
                  <div className="text-xl font-semibold text-emerald-800">{stats.right}</div>
                </div>
                <div className="rounded-2xl border border-red-200 bg-red-50 p-3">
                  <div className="text-xs text-red-700">Wrong</div>
                  <div className="text-xl font-semibold text-red-800">{stats.wrong}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs text-slate-500">Unanswered</div>
                  <div className="text-xl font-semibold text-slate-900">{stats.un}</div>
                </div>
                <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-3">
                  <div className="text-xs text-indigo-700">Accuracy</div>
                  <div className="text-xl font-semibold text-indigo-800">{stats.accuracy}%</div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 overflow-hidden" data-tour="results-table">
                <table className="w-full min-w-[680px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-600">
                      <th className="px-4 py-3">Question</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Selected</th>
                      <th className="px-4 py-3">Exam ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {childs.length === 0 ? (
                      <tr>
                        <td className="px-4 py-5 text-slate-600" colSpan={4}>
                          No question-level details on this attempt.
                        </td>
                      </tr>
                    ) : (
                      childs.map((child, idx) => {
                        const rw = String(child?.right_or_wrong ?? "Unanswered").trim();
                        const examId = child?.exam_id ?? child?.examId ?? child?.id ?? "—";
                        return (
                          <tr key={`${examId}-${idx}`}>
                            <td className="px-4 py-3 text-sm text-slate-900">
                              {child?.exam_name || child?.question || `Question ${idx + 1}`}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700">{rw || "Unanswered"}</td>
                            <td className="px-4 py-3 text-sm text-slate-700">{getSelectedChoice(child)}</td>
                            <td className="px-4 py-3 text-sm text-slate-700">{String(examId)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
